import _ from 'lodash'
import dayjs from 'dayjs'
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { useResizeDetector } from 'react-resize-detector'
import { useTranslation } from 'react-i18next'
import { useAudioRecorder } from '@/hooks/useAudioRecorder'
import ImageMapper, { MapAreas } from 'react-img-mapper'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import { styled } from '@mui/material/styles'

import cardioVascularImgUrl from '@/assets/img/cardio-vascular.jpg'

import DeviceSelectionDialog from '@/components/ui/dialogs/DeviceSelectionDialog'
import { HDStethEcgPanel } from '@/components/hd-steth/hd-steth-ecg-panel'
import type { HDStethEcgSource } from '@/utils/hdSteth/ecg-types'

import colors from '@/assets/scss/_themes-vars.module.scss'

export type StethoscopeProps = {
  /** Present only for the selected local or remote HD Steth source. */
  ecg?: HDStethEcgSource
  audioInputMediaDevices: MediaDeviceInfo[]
  externalAudioInputActive?: boolean
  externalSelectedDeviceId?: string
  isStreamer: boolean
  onRecordStop?: (file: File) => void
  onSelectDevice?: (deviceId: string) => void
  onStopMicro?: () => void
  patientInfo: {
    firstName: string
    lastName: string
  }
  renderAudio: () => JSX.Element | null
  renderVisualizer: () => JSX.Element
  stethoscopes: Array<{
    id: string
    device: {
      deviceId: string
      label: string
      kind: MediaDeviceKind
    }
    customName: string
    iconURL: string
    type: string
  }>
  stream: MediaStream | null
}

export type StethoscopeHandle = {
  /** Returns false if stream is not available */
  startRecording: () => boolean
  stopRecording: () => void
  stopDevice: () => void
  isRecording: boolean
}

const PlayerControlButton = styled(Button)({
  backgroundColor: colors.red500,
  boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
  borderRadius: '17px',
  color: '#FFF',
  fontSize: '14px',
  lineHeight: '16px',
  padding: '10px 15px',
  '&:hover': {
    backgroundColor: colors.red500,
  },
})

export const Stethoscope = forwardRef<StethoscopeHandle, StethoscopeProps>(function Stethoscope(props, ref) {
  const { audioInputMediaDevices, externalAudioInputActive, externalSelectedDeviceId, isStreamer, onRecordStop, onSelectDevice, onStopMicro, patientInfo, renderAudio, renderVisualizer, stethoscopes, stream } =
    props
  const { t } = useTranslation()
  const { isRecording, startRecording, stopRecording } = useAudioRecorder()

  const { width: imageMapperWrapperWidth, ref: imageMapperWrapperRef } = useResizeDetector()

  const [audioInputActive, setAudioInputActive] = useState<boolean>(false)
  const [deviceSelectionDialogOpen, setDeviceSelectionDialogOpen] = useState<boolean>(false)
  const [imageNatureDimensions, setImageNatureDimensions] = useState<{ width: number; height: number } | null>(null)
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>()
  const [selectedImageMapperArea, setSelectedImageMapperArea] = useState<(MapAreas & { label?: string }) | null>(null)

  const devices = useMemo(
    () =>
      _.concat(
        _.map(stethoscopes, (stethoscope) => ({
          id: stethoscope.device.deviceId,
          iconUrl: stethoscope.iconURL,
          isCustom: true,
          isSelected: stethoscope.device.deviceId === selectedDeviceId,
          label: stethoscope.customName,
          type: stethoscope.device.kind,
        })),
        _.map(audioInputMediaDevices, ({ deviceId, label, kind }: MediaDeviceInfo) => ({
          id: deviceId,
          label: label,
          isCustom: false,
          isSelected: deviceId === selectedDeviceId,
          type: kind,
        }))
      ),
    [audioInputMediaDevices, stethoscopes, selectedDeviceId]
  )

  const imageMapperMap = useMemo(() => {
    const areas = [
      { id: 'aortic', label: t('component.group.stethoscope.imageMapper.area.aortic.label'), shape: 'rect', coords: [15, 10, 240, 168] },
      { id: 'pulmonary', label: t('component.group.stethoscope.imageMapper.area.pulmonary.label'), shape: 'rect', coords: [673, 10, 896, 168] },
      { id: 'tricuspid', label: t('component.group.stethoscope.imageMapper.area.tricuspid.label'), shape: 'rect', coords: [15, 400, 240, 560] },
      { id: 'mitral', label: t('component.group.stethoscope.imageMapper.area.mitral.label'), shape: 'rect', coords: [674, 400, 896, 560] },
    ]
    return {
      name: 'cardio-vascular',
      areas: _.map(areas, (area) => _.merge(area, { strokeColor: area.id === selectedImageMapperArea?.id ? 'darkred' : 'transparent', lineWidth: 3 })),
    }
  }, [selectedImageMapperArea, t])

  useEffect(() => {
    const img = new Image()
    img.src = cardioVascularImgUrl

    img.onload = () => {
      setImageNatureDimensions({ width: img.naturalWidth, height: img.naturalHeight })
    }
  }, [])

  // Sync internal state when external props change (e.g. ELARA selects a device)
  useEffect(() => {
    if (externalAudioInputActive !== undefined) setAudioInputActive(externalAudioInputActive)
  }, [externalAudioInputActive])

  useEffect(() => {
    if (externalSelectedDeviceId !== undefined) setSelectedDeviceId(externalSelectedDeviceId)
  }, [externalSelectedDeviceId])

  const handleRecordingStopped = ({ blob, name }: { blob: Blob; name: string }) => {
    const filename = !!selectedImageMapperArea
      ? _.join([selectedImageMapperArea.label || '', _.trim(`${patientInfo.firstName} ${patientInfo.lastName}`), dayjs().format('DD/MM/YYYY HH[h]mm')], ', ') +
      '.mp3'
      : `stethoscope-${name}`
    const file = new File([blob], filename, { type: 'audio/mpeg' })
    onRecordStop?.(file)
    if (!!selectedImageMapperArea) setSelectedImageMapperArea(null)
  }

  const handleSelectDevice = useCallback(
    (deviceId: string) => {
      setSelectedDeviceId(deviceId)
      setAudioInputActive(true)
      setDeviceSelectionDialogOpen(false)
      onSelectDevice?.(deviceId)
    },
    [onSelectDevice]
  )

  const handleSelectImageMapperArea = (area: MapAreas & { label?: string }) => {
    if (selectedImageMapperArea?.id !== area.id && !isRecording) {
      setSelectedImageMapperArea(area)
      handleStartRecording()
    }
  }

  const handleStartMicro = () => {
    setDeviceSelectionDialogOpen(true)
  }

  const handleStopMicro = () => {
    setAudioInputActive(false)
    setSelectedDeviceId(undefined)
    // stop recording if we click on stop micro and we are recording
    if (isRecording) stopRecording(handleRecordingStopped)
    onStopMicro?.()
  }

  const handleStartRecording = (): boolean => {
    if (!stream) return false
    startRecording(stream, handleRecordingStopped)
    return true
  }

  const handleStopRecording = () => {
    stopRecording(handleRecordingStopped)
  }

  useImperativeHandle(ref, () => ({
    startRecording: handleStartRecording as () => boolean,
    stopRecording: handleStopRecording,
    stopDevice: handleStopMicro,
    isRecording,
  }))

  const handleToggleDeviceSelectionDialog = () => {
    setDeviceSelectionDialogOpen((prevState) => !prevState)
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ overflow: 'hidden' }}>
        <Grid container>
          <Grid item xs={12} sm={audioInputActive ? 6 : 12}>
            {renderVisualizer()}
            {renderAudio()}
            {isStreamer && (
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ p: '16px 25px' }}>
                {audioInputActive ? (
                  <>
                    {!isRecording && (
                      <PlayerControlButton onClick={handleStartRecording} size="small">
                        {t('page.liveConsultation.stethoscope.startRecordingButton.label')}
                      </PlayerControlButton>
                    )}
                    {isRecording && (
                      <PlayerControlButton onClick={handleStopRecording} size="small">
                        {t('page.liveConsultation.stethoscope.stopRecordingButton.label')}
                      </PlayerControlButton>
                    )}
                    <PlayerControlButton onClick={handleStopMicro} size="small">
                      {t('common:button.done.label')}
                    </PlayerControlButton>
                  </>
                ) : (
                  <PlayerControlButton onClick={handleStartMicro} size="small">
                    {t('common:button.start.label')}
                  </PlayerControlButton>
                )}
              </Stack>
            )}
            {!isStreamer && stream && stream.getAudioTracks().length && (
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ p: '16px 25px' }}>
                {!isRecording && (
                  <PlayerControlButton onClick={handleStartRecording} size="small">
                    {t('page.liveConsultation.stethoscope.startRecordingButton.label')}
                  </PlayerControlButton>
                )}
                {isRecording && (
                  <PlayerControlButton onClick={handleStopRecording} size="small">
                    {t('page.liveConsultation.stethoscope.stopRecordingButton.label')}
                  </PlayerControlButton>
                )}
              </Stack>
            )}
          </Grid>
          {audioInputActive && (
            <Grid
              item
              xs={12}
              sm={6}
              ref={imageMapperWrapperRef}
              container
              alignItems="center"
              justifyContent="center"
              sx={{
                area: {
                  cursor: isRecording ? 'not-allowed' : 'pointer',
                },
              }}
            >
              {!!imageMapperWrapperWidth && !!imageNatureDimensions && (
                <ImageMapper
                  imgWidth={imageNatureDimensions.width}
                  height={imageMapperWrapperWidth * (imageNatureDimensions.height / imageNatureDimensions.width)}
                  map={imageMapperMap}
                  onClick={handleSelectImageMapperArea}
                  src={cardioVascularImgUrl}
                  stayHighlighted
                  width={imageMapperWrapperWidth}
                />
              )}
            </Grid>
          )}
        </Grid>
        {props.ecg && <HDStethEcgPanel source={props.ecg} />}
      </Paper>
      <DeviceSelectionDialog
        devices={devices}
        onClose={handleToggleDeviceSelectionDialog}
        onSelectDevice={handleSelectDevice}
        open={deviceSelectionDialogOpen}
        showAudioOutput={false}
        showVideoInput={false}
        showStethoscopeHint
        context="stethoscope"
      />
    </Box>
  )
})

export default Stethoscope
